import { type ReactNode, useState } from 'react';
import { Icons } from './icons';
import { Button, IconButton } from './form';

export function Badge({ children, state }: { children: ReactNode; state?: string }): JSX.Element {
  const style = state
    ? {
        color: `var(--tbm-state-${state}-fg, var(--tbm-muted))`,
        background: `var(--tbm-state-${state}-bg, transparent)`,
      }
    : undefined;
  return (
    <span className="tbm-badge" data-state={state} style={style}>
      {children}
    </span>
  );
}

export function StatePill({ state, children }: { state: string; children?: ReactNode }): JSX.Element {
  return <Badge state={state}>{children ?? state}</Badge>;
}

export function Stat({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="tbm-card tbm-stat">
      <p className="tbm-stat__label">{label}</p>
      <p className="tbm-stat__value">{value}</p>
    </div>
  );
}

export function Avatar({ src, name }: { src?: string | null; name: string }): JSX.Element {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <span className="tbm-avatar">
      {src ? <img src={src} alt="" /> : initial}
    </span>
  );
}

export function Skeleton(): JSX.Element {
  return <div className="tbm-skeleton" />;
}

export function RelativeTime({ value }: { value?: string | null }): JSX.Element {
  if (!value) return <span className="muted">—</span>;
  const date = new Date(value);
  const delta = Date.now() - date.getTime();
  const minutes = Math.round(delta / 60000);
  const label =
    minutes < 1 ? 'à l’instant' : minutes < 60 ? `il y a ${minutes} min` : date.toLocaleString('fr-FR');
  return <span title={date.toLocaleString('fr-FR')}>{label}</span>;
}

export function IdChip({ value }: { value: string }): JSX.Element {
  return <span className="tbm-chip tbm-id">{value}</span>;
}

export function JsonBlock({ value }: { value: unknown }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
        {open ? 'Masquer JSON' : 'Afficher JSON'}
      </Button>
      {open ? <pre className="tbm-json">{JSON.stringify(value, null, 2)}</pre> : null}
    </div>
  );
}

export function CopyButton({ text }: { text: string }): JSX.Element {
  const [done, setDone] = useState(false);
  return (
    <IconButton
      type="button"
      aria-label="Copier"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
    >
      {done ? <Icons.check /> : <Icons.copy />}
    </IconButton>
  );
}

export function OneTimeSecret({ value }: { value: string }): JSX.Element {
  return (
    <div className="tbm-secret">
      <code className="tbm-id">{value}</code>
      <CopyButton text={value} />
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }): JSX.Element {
  return <div className="tbm-empty">{children}</div>;
}

export function ErrorNote({ children }: { children: ReactNode }): JSX.Element {
  return <div className="tbm-error">{children}</div>;
}

export function Loading({ children = 'Chargement…' }: { children?: ReactNode }): JSX.Element {
  return <div className="tbm-loading">{children}</div>;
}

export function ScopeList({
  required = [],
  granted = [],
  missing = [],
}: {
  required?: string[];
  granted?: string[];
  missing?: string[];
}): JSX.Element {
  const miss = new Set(missing);
  const grant = new Set(granted);
  const items = required.length ? required : [...grant];
  return (
    <div className="tbm-scopes">
      {items.map((scope) => (
        <span key={scope} className={`tbm-chip ${miss.has(scope) ? 'tbm-scope--missing' : grant.has(scope) ? 'tbm-scope--granted' : ''}`}>
          {scope}
        </span>
      ))}
    </div>
  );
}
